/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { ContentEditableEvent, getUrlSync, RichTextComponent, Text } from '@msdyn365-commerce/core';
import { Address } from '@msdyn365-commerce/retail-proxy';
// import { createProspectAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/BusinessPartnersDataActions.g';
import { EmailRegex } from '@msdyn365-commerce-modules/retail-actions';
import { getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { IBusinessSignUpConfig, IBusinessSignUpProps } from './business-sign-up.props.autogenerated';
import { SignUpButton, SignUpError, SignUpInput, SignUpLabel } from './components';
import { IAPIConfiguration,
     INewAccountEntity } from '../../actions/AlternateItemsEntity.g';
import { gCCreateAccountRequestAsync } from '../../actions/createNewAccountRequest';
import { Buffer } from 'buffer';

export interface IBusinessSignUpExtendedProps extends IBusinessSignUpProps<IBusinessSignUpConfig> {
    formItems?: IFormItem[];
}

export interface IBusinessSignUpItem {
    wrapper: INodeProps;
    isAddressInput?: boolean;
    id: string;
    label: React.ReactNode;
    errorMessage: React.ReactNode;
    input?: React.ReactNode;
}

export interface IBusinessState {
    isReady: boolean;
    formItems: IFormItem[];
    addressHasErrors: boolean;
    showAddressErrors: boolean;
    hasSubmitErrors: boolean;
    isLoading: boolean;
    captchaToken?: string;
}

export interface IBusinessSignUpLocalAccount {
    FormWrapper: INodeProps;
    items: IBusinessSignUpItem[];
    buttons: React.ReactNode[];
    errorMessage: React.ReactNode[];
    disclaimer: React.ReactNode;
}


export interface IBusinessSignUpViewProps {
    className: string;
    businessSignUp: IModuleProps;
    ContainerWrapper: INodeProps;
    heading: React.ReactNode;
    businessSignUpForm: IBusinessSignUpLocalAccount;
    formItems: IFormItem[];
}

export interface IFormItem {
    id: string;
    type: InputType;
    label: string;
    value: string;
    maxChars?: string;
    isRequired: boolean;
    pattern?: string;
    address?: Address;
    customClass?: string;
    fieldReference?: React.RefObject<HTMLInputElement>;
}

export enum InputType {
    // eslint-disable-next-line @typescript-eslint/no-shadow -- Text enum doesn't override the imported Text
    Text = 'text',
    Address = 'address',
    Email = 'email',
    TextArea = 'textarea'
}

export enum InputID {
    FirstName = 'FirstName',
    LastName = 'LastName',
    Email = 'CompanyEmail',
    CompanyName = 'CompanyName',
    Address = 'CompanyAddress',
    JobTitle = 'JobTitle',
    Size = 'CompanySize',
    Department = 'Department',
    Comment = 'Comment',
    TaxIDorLicense = 'TaxIDorLicense',
    Website = 'Website'
}

export const renderInputSection = (
    inputs: IFormItem[],
    className: string,
    needToReset: boolean,
    onInputChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void,
    onKeyInputDown: (event: React.KeyboardEvent<HTMLInputElement> | undefined) => void,
    onKeyTextAreaDown: (event: React.KeyboardEvent<HTMLTextAreaElement> | undefined) => void,
    showErrors: boolean,
    addressForm?: React.ReactNode,
    updateAddress?: (errors: boolean, currAddress?: Address) => void
): IBusinessSignUpItem[] => {
    return inputs.map((element, index) => {
        const { id, type, label, maxChars, isRequired, pattern, customClass, value, fieldReference } = element;

        const baseSignUpItem: IBusinessSignUpItem = {
            wrapper: {
                className: classnames(className, `${className}-${id}`, customClass)
            },
            id,
            label: <SignUpLabel {...{ id, forId: id, className, text: label }} />,
            errorMessage: <SignUpError {...{ className, type: 'item' }} />
        };

        if (type === InputType.Address) {
            if (addressForm) {
                baseSignUpItem.isAddressInput = true;
                baseSignUpItem.input = (
                    <>
                        {React.cloneElement(addressForm as React.ReactElement, {
                            updateForm: updateAddress,
                            hasError: showErrors,
                            isRequired: isRequired === undefined ? false : isRequired,
                            resetAddress: needToReset
                        })}
                    </>
                );
            } else {
                // If the address slot is not filled, remove this input from the form
                inputs.splice(index, 1);
            }
        } else {
            baseSignUpItem.input = (
                <SignUpInput
                    {...{
                        id,
                        type,
                        value,
                        maxLength: maxChars,
                        pattern,
                        className,
                        isRequired: isRequired === undefined ? false : isRequired,
                        onChange: onInputChange,
                        onKeyDownInputCustom: onKeyInputDown,
                        onKeyDownTextAreaCustom: onKeyTextAreaDown,
                        fieldReference
                    }}
                />
            );
        }

        return baseSignUpItem;
    });
};

/**
 *
 * Business Request Form Modules
 * Form that a business can use to submit a request to become a partner with Microsoft.
 * @extends {React.Component<IBusinessSignUpProps<IBusinessSignUpConfig>>}
 */
class BusinessSignUp extends React.Component<IBusinessSignUpExtendedProps, IBusinessState> {
    // Used as default data
    public BusinessSignUpDefaultInputs: IFormItem[] = [
        {
            id: InputID.FirstName,
            type: InputType.Text,
            label: this.props.resources.firstNameLabel,
            value: '',
            isRequired: true,
            customClass: 'width-25',
            maxChars:this.props.resources.firstNameMaxLength
        },
        {
            id: InputID.LastName,
            type: InputType.Text,
            label: this.props.resources.lastNameLabel,
            value: '',
            isRequired: true,
            customClass: 'width-25',
            maxChars:this.props.resources.lastNameMaxLength
        },
        {
            id: InputID.Email,
            type: InputType.Email,
            label: this.props.resources.emailAddressLabel,
            value: '',
            isRequired: true,
            pattern: EmailRegex.defaultRegex.source,
            customClass: 'width-50',
            maxChars:this.props.resources.emailAddressMaxLength
        },
        {
            id: InputID.CompanyName,
            type: InputType.Text,
            label: this.props.resources.companyLabel,
            value: '',
            isRequired: true,
            customClass: 'width-50',
            maxChars:this.props.resources.companyMaxLength
        },
        {
            id: InputID.TaxIDorLicense,
            type: InputType.Text,
            label: this.props.resources.taxidorlicenseLabel,
            value: '',
            isRequired: true,
            customClass: 'width-25',
            maxChars:this.props.resources.taxidorlicenseMaxLength
        },
        {
            id: InputID.Size,
            type: InputType.Text,
            label: this.props.resources.companySizeLabel,
            value: '',
            isRequired: false,
            customClass: 'width-25',
            maxChars:this.props.resources.companySizeMaxLength
        },
        {
            id: InputID.Department,
            type: InputType.Text,
            label: this.props.resources.departmentLabel,
            value: '',
            isRequired: false,
            customClass: 'width-50',
            maxChars:this.props.resources.departmentMaxLength
        },
        {
            id: InputID.JobTitle,
            type: InputType.Text,
            label: this.props.resources.jobTitleLabel,
            value: '',
            isRequired: false,
            customClass: 'width-50',
            maxChars:this.props.resources.jobTitleMaxLength
        },
        {
            id: InputID.Website,
            type: InputType.Text,
            label: this.props.resources.websiteLabel,
            value: '',
            isRequired: true,
            customClass: 'width-50',
            maxChars:this.props.resources.websiteMaxLength
        },
        {
            id: InputID.Address,
            type: InputType.Address,
            label: this.props.resources.companyAddressLabel,
            value: '',
            isRequired: false,
            customClass: 'width-100',
            maxChars:this.props.resources.companyAddressMaxLength
        },
        {
            id: InputID.Comment,
            type: InputType.TextArea,
            label: this.props.resources.commentLabel,
            value: '',
            isRequired: false,
            customClass: 'width-100',
            maxChars:this.props.resources.commentMaxLength
        }
    ];

    private readonly formChildReference: React.RefObject<HTMLInputElement> = React.createRef<HTMLInputElement>();

    private readonly moduleClassName: string = 'ms-business-sign-up';

    private needsToReset: boolean = false;

    private readonly telemetryContent: ITelemetryContent;

    constructor(props: IBusinessSignUpExtendedProps) {
        super(props);

        this.state = {
            isReady: false,
            formItems: this.props.formItems || this._addedReference(),
            addressHasErrors: false,
            showAddressErrors: false,
            hasSubmitErrors: false,
            isLoading: false
        };
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
    }

    private handleLoaded = () => {
        //@ts-ignore
        if(window && window.grecaptcha){
            //@ts-ignore
                window.grecaptcha.ready(()=> {
                    const { context } = this.props;
            //@ts-ignore
                    window.grecaptcha
                .execute(context.app.config.googleCaptchaSiteKey, { action: "onSubmit" })
                .then(function(response:string)  {
                    getCaptchaToken(response);
                });
            });
        }
            const getCaptchaToken = (token: string) =>{
                this.setState({captchaToken: token});
            };
      };

    public componentDidMount(): void {
        const { context } = this.props;
        this.formChildReference.current?.focus();
        const script = document.createElement("script");
        script.src = `https://www.google.com/recaptcha/api.js?render=${context.app.config.googleCaptchaSiteKey}`;
        script.addEventListener("load", this.handleLoaded);
        document.body.appendChild(script);
    }

    public shouldComponentUpdate(nextProps: IBusinessSignUpExtendedProps, nextState: IBusinessState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element {
        const { config } = this.props;
        const { formItems } = this.state;
        const { className, heading } = config;

        const viewProps = {
            ...this.props,
            businessSignUp: {
                moduleProps: this.props,
                className: classnames(this.moduleClassName, className)
            },
            ContainerWrapper: {
                className: `${this.moduleClassName}__container`
            },
            heading: (
                <>
                <Text
                    className={`${this.moduleClassName}__heading`}
                    {...heading}
                    editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                />
                {this.state.isLoading ? <div className='ms-order-upload__loading' id='loading' >Loading...</div>: <div></div> }
                </>
            ),
            businessSignUpForm: this._renderForm(),
            formItems
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /**
     * Handle the heading change event.
     * @param event - Content Editable Event.
     */
    public handleHeadingChange = (event: ContentEditableEvent): void => {
        this.props.config.heading.text = event.target.value;
    };

    private _renderForm(): IBusinessSignUpLocalAccount {
        const { config, resources, slots } = this.props;
        const { showAddressErrors, hasSubmitErrors, isReady, formItems } = this.state;
        const {
            signUpButtonArialabel,
            signUpButtonText,
            cancelButtonArialabel,
            cancelButtonText,
            errorMessage,
            partialAddressErrorMessage
        } = resources;
        const addressForm = (slots && slots.addressSlot && slots.addressSlot.length > 0 && slots.addressSlot[0]) || undefined;


        const form = {
            FormWrapper: {
                className: `${this.moduleClassName}__form `
            },
            items: renderInputSection(
                formItems,
                `${this.moduleClassName}__form-item`,
                this.needsToReset,
                this._onInputChange,
                this._onKeyInputDown,
                this._onKeyTextAreaDown,
                showAddressErrors,
                addressForm,
                this._onAddressChange
            ),
            buttons: [
                <><div
                className="g-recaptcha"
                data-sitekey="6Ld9GTglAAAAAJ0iK6gFPLhGVDlZMBSuABHwC5wP"
                data-size="invisible"
              ></div>
                <SignUpButton
                    key='continue'
                    id='continue'
                    className={`${this.moduleClassName}__create-button`}
                    // link={successPageURL}
                    ariaLabel={signUpButtonArialabel}
                    text={signUpButtonText}
                    disabled={!isReady}
                    onClick={this._onSubmit}
                    telemetryContent={this.telemetryContent}
                /></>,
                <SignUpButton
                    key='cancel'
                    id='cancel'
                    className={`${this.moduleClassName}__cancel-button`}
                    ariaLabel={cancelButtonArialabel}
                    text={cancelButtonText}
                    onClick={this._onCancel}
                    telemetryContent={this.telemetryContent}
                />
            ],
            errorMessage: [
                hasSubmitErrors && <SignUpError key='error' id='error' className={this.moduleClassName} message={errorMessage} />,
                showAddressErrors && (
                    <SignUpError key='error' id='error' className={this.moduleClassName} message={partialAddressErrorMessage} />
                )
            ],
            disclaimer: config.disclaimer && (
                <RichTextComponent className={`${this.moduleClassName}__disclaimer`} text={config.disclaimer} />
            )
        };

        if (this.needsToReset) {
            this.needsToReset = false;
        }

        return form;
    }

    private readonly _onInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        /*Restrict entry for special characters for firstName, LastName and TaxIDorLicense fields*/
        if (event && event.target.value) {
            const labelID = event.currentTarget.id;
            switch (labelID) {
            case 'FirstName':
            case 'LastName':
            case 'TaxIDorLicense':
            event.target.value = (event.target as HTMLInputElement).value.replace(/[^0-9a-zA-Z ]+/ig, '');
                break;
            }
       }
        const newForm = this.state.formItems;
        newForm.forEach(item => {
            if (item.id === event.target.id) {
                item.value = event.target.value;
            }
        });

        // Check if form is ready, update state object with latest values, values have changed so reset hasSubmitErrros
        this.setState({
            isReady: this._isFormReady(),
            formItems: newForm,
            hasSubmitErrors: false
        });
    };

    private readonly _onKeyInputDown = (event: React.KeyboardEvent<HTMLInputElement> | undefined): void => {
        if(event && (event.key === 'Tab'))
        {
            if(event.currentTarget && event.currentTarget.value.trim() === '' && event.currentTarget.getAttribute('aria-required') === 'true')
            {
                if(event.currentTarget.parentElement && event.currentTarget.parentElement.childNodes[2])
                {
                    const labelID = event.currentTarget.id;
                    switch (labelID) {
                        case 'FirstName':
                            event.currentTarget.parentElement.childNodes[2].firstChild!.textContent = 'Please enter First Name';
                            break;
                        case 'LastName':
                            event.currentTarget.parentElement.childNodes[2].firstChild!.textContent = 'Please enter Last Name';
                            break;
                        case 'CompanyEmail':
                            event.currentTarget.parentElement.childNodes[2].firstChild!.textContent = 'Please enter Email address';
                            break;
                        case 'CompanyName':
                            event.currentTarget.parentElement.childNodes[2].firstChild!.textContent = 'Please enter Company';
                            break;
                        case 'TaxIDorLicense':
                            event.currentTarget.parentElement.childNodes[2].firstChild!.textContent = 'Please enter Tax ID or License';
                            break;
                        case 'Website':
                            event.currentTarget.parentElement.childNodes[2].firstChild!.textContent = 'Please enter Website';
                            break;
                    }
                }
            }else if(event.currentTarget && event.currentTarget.value.trim() !== '' && event.currentTarget.id === 'CompanyEmail' ){
                const regex = /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;
                if(regex.test(event.currentTarget.value) === false)
                {
                    if(event.currentTarget.parentElement && event.currentTarget.parentElement.childNodes[2])
                    {
                        event.currentTarget.parentElement.childNodes[2].firstChild!.textContent ='Please enter a valid email address';
                    }
                }
                else
                {
                    if(event.currentTarget.parentElement && event.currentTarget.parentElement.childNodes[2])
                    {
                        event.currentTarget.parentElement.childNodes[2].firstChild!.textContent ='';
                    }
                }
            }
            else if(event.currentTarget && event.currentTarget.value.trim() !== '' && event.currentTarget.id === 'Website' ){
               const regex=new RegExp("^(https?://)?(www\\.)?([-a-z0-9]{1,63}\\.)*?[a-z0-9][-a-z0-9]{0,61}[a-z0-9]\\.[a-z]{2,6}(/[-\\w@\\+\\.~#\\?&/=%]*)?$");
                if(regex.test(event.currentTarget.value) === false)
                {
                    if(event.currentTarget.parentElement && event.currentTarget.parentElement.childNodes[2])
                    {
                        event.currentTarget.parentElement.childNodes[2].firstChild!.textContent ='Please enter a valid website address';
                    }
                }
                else
                {
                    if(event.currentTarget.parentElement && event.currentTarget.parentElement.childNodes[2])
                    {
                        event.currentTarget.parentElement.childNodes[2].firstChild!.textContent ='';
                    }
                }
            }
            else if(event.currentTarget && event.currentTarget.parentElement && event.currentTarget.parentElement.childNodes[2]){
                event.currentTarget.parentElement.childNodes[2].firstChild!.textContent ='';
            }
        }
         else{
                /*Removing tab Spaces for Email Field */
            if(event && (event.key === " ") && event.currentTarget.id === 'CompanyEmail'){
                event.currentTarget.value=event.currentTarget.value.trim();
            }
        }
    };

    private readonly _onKeyTextAreaDown = (event: React.KeyboardEvent<HTMLTextAreaElement> | undefined): void => {
        if(event && event.key === 'Tab')
        {
            if(event.currentTarget && event.currentTarget.value.trim() === '' && event.currentTarget.getAttribute('aria-required') === 'true')
            {
                if(event.currentTarget.parentElement && event.currentTarget.parentElement.childNodes[2])
                {
                    event.currentTarget.parentElement.childNodes[2].firstChild!.textContent ='required field';
                    event.currentTarget.focus();
                }
            }
            else if(event.currentTarget && event.currentTarget.parentElement && event.currentTarget.parentElement.childNodes[2]){
                event.currentTarget.parentElement.childNodes[2].firstChild!.textContent ='';
            }
        }
    };

    private _addedReference(): IFormItem[] {
        const newFormFields = this.BusinessSignUpDefaultInputs;
        newFormFields[0].fieldReference = this.formChildReference;
        return newFormFields;
    }

    private readonly _onAddressChange = (errors: boolean, currentAddress?: Address): void => {
        // Find address item
        const newForm = this.state.formItems;
        const addressItem = newForm.find(item => {
            return item.type === InputType.Address;
        });

        // If found --> set to currentAddress
        if (addressItem) {
            addressItem.address = currentAddress;

            // Update state with new values and set errors to false if address is not required field
            this.setState({
                isReady: this._isFormReady(),
                formItems: newForm,
                addressHasErrors: errors,
                showAddressErrors: false
            });
        }
    };

    private readonly _isFormReady = (): boolean => {
        const { formItems } = this.state;

        for (const currentInput of formItems) {
            // Will return FALSE if:
            //  - input field is required
            //      IF is address AND addressHasErrors === true
            //      ELSE value is undefined OR empty
            //  OR
            //  - pattern provided and value does not match pattern

            if (currentInput.isRequired) {
                if (currentInput.type === InputType.Address) {
                    if (this.state.addressHasErrors) {
                        return false;
                    }
                } else if (currentInput.value === undefined || currentInput.value === '') {
                    return false;
                }
            }
            if (currentInput.pattern !== undefined && currentInput.value.match(currentInput.pattern) === null) {
                return false;
            }
        }

        return true;
    };

    private _setCaptchToken = async() =>{
          //@ts-ignore
          if(window && window.grecaptcha){

            //@ts-ignore
                // window.grecaptcha.ready(()=> {
                    const { context } = this.props;
            //@ts-ignore
             const resp =   await    window.grecaptcha
                .execute(context.app.config.googleCaptchaSiteKey, { action: "onSubmit" });
                this.setState({captchaToken: resp});
        }
    };


    private readonly _onSubmit = async (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        if (this.state.addressHasErrors) {
            this.setState({
                showAddressErrors: true
            });

            event.preventDefault();
            return;
        }

        await this._setCaptchToken();

        this.setState({isLoading:true});
        const { context } = this.props;

        // Check for matching id and if not empty
        const firstName = this._getItem(InputID.FirstName);
        const lastName = this._getItem(InputID.LastName);
        const companyName = this._getItem(InputID.CompanyName);
        const companyEmail = this._getItem(InputID.Email);
        const companyAddress = this._getItem(InputID.Address, true);
        const jobTitle = this._getItem(InputID.JobTitle);
        const department = this._getItem(InputID.Department);
        const companySize = this._getItem(InputID.Size);
        const comments = this._getItem(InputID.Comment);
        const taxID = this._getItem(InputID.TaxIDorLicense);
        const website = this._getItem(InputID.Website);
        // const extensionProp:CommerceProperty[] = new Array();
        // API gets email as part of the address object, but I do not use the email input from the address component, thus we need to copy it over
        if (companyEmail && companyEmail.value && companyAddress && companyAddress.address) {
            companyAddress.address.Email = companyEmail.value;
        }

        const newAccount:INewAccountEntity ={
            firstname: firstName && firstName.value,
            lastname: lastName && lastName.value,
            companyname: companyName && companyName.value,
            address1_line1: companyAddress && companyAddress.address && companyAddress.address.Street,
            address1_city: companyAddress && companyAddress.address && companyAddress.address.City,
            address1_country: companyAddress && companyAddress.address && companyAddress.address.ThreeLetterISORegionName,
            address1_postalcode: companyAddress && companyAddress.address && companyAddress.address.ZipCode,
            address1_stateorprovince: companyAddress && companyAddress.address && companyAddress.address.State,
            emailaddress1: companyEmail && companyEmail.value,
            jobtitle: jobTitle && jobTitle.value,
            websiteurl: website && website.value,
            telephone1: companyAddress && companyAddress.address && companyAddress.address.Phone,
            mobilephone: companyAddress && companyAddress.address && companyAddress.address.Phone,
            address1_composite:companyAddress && companyAddress.address && `${companyAddress.address.Street },\r\n${ companyAddress.address.City } ${ companyAddress.address.ZipCode}`,
            gc_einnumber:taxID && taxID.value,
            gc_department: department && department.value,
            gc_companysize: companySize && companySize.value,
            msdyn_company: context.app.config.crmCompanyGuid,
            leadsourcecode:context.app.config.leadSourceCode
        };

        const apiConfig:IAPIConfiguration ={
            APIUrl: Buffer.from(context.app.config.crmLeadsUrl,'utf8').toString('base64'),
            ClientId: Buffer.from(context.app.config.crmClientId,'utf8').toString('base64'),
            ClientSecret: Buffer.from(context.app.config.crmClientSecret,'utf8').toString('base64'),
            GrantType: "client_credentials",
            LoginUrl: Buffer.from(`https://login.microsoftonline.com/${context.app.config.crmTenantId}/oauth2/token`,'utf8').toString('base64'),
            Resource: Buffer.from(context.app.config.crmBaseUrl,'utf8').toString('base64'),
            AnnotationUrl: Buffer.from(context.app.config.crmAnnotationUrl,'utf8').toString('base64')
        };

        const successPageURL =
        (this.props.config.confirmationLink && this.props.config.confirmationLink.linkUrl && this.props.config.confirmationLink.linkUrl.destinationUrl) ||
        getUrlSync('home', this.props.context.actionContext);

        gCCreateAccountRequestAsync({ callerContext: context.actionContext },newAccount,(comments? comments.value : ''),this.state.captchaToken!,context.app.config.googleCaptchaSecretKey,apiConfig).then(function(response){
                if(response && response.Result && response.Result.toLowerCase().includes('success'))
                {
                    loadFalse();
                    if(successPageURL)
                    {
                        window.location.href = successPageURL;
                    }
                }
                else{
                    console.log(response.Result);
                    event.preventDefault();
                    setTrueErrorFlag();
                    loadFalse();
                }
            }).catch((resp) => {
                loadFalse();
            console.log(resp);
            event.preventDefault();
            this.setState({
                hasSubmitErrors: true,
                isReady: false
            });
        });

        const loadFalse=() =>{
            this.setState({isLoading:false});
        };

        const setTrueErrorFlag=() =>{
            this.setState({
                hasSubmitErrors: true,
                isReady: false
            });
        };
    };

    private readonly _onCancel = () => {
        // Reset values of the form
        const cleanForm = this.state.formItems;
        cleanForm.forEach(input => {
            input.value = '';
        });

        // Set reset flag so address input will get reset on next renderInputSection() call
        this.needsToReset = true;

        this.setState({
            isReady: false,
            formItems: cleanForm,
            addressHasErrors: true,
            showAddressErrors: false,
            hasSubmitErrors: false
        });
    };

    private readonly _getItem = (id: string, isAddress: boolean = false) => {
        if (isAddress) {
            return this.state.formItems.find(item => {
                return item.id === id && item.address;
            });
        }
        return this.state.formItems.find(item => {
            return item.id === id && item.value !== '';
        });
    };
}

export default BusinessSignUp;
